/**
 * Release notes for the "What's New" drawer and the new-version notification.
 *
 * Add one Markdown file per release to this folder, named after its version
 * (`3.5.0.md`), and bump `version` in client/package.json in the same change.
 * The highest version is shown as the latest release. No code edit is needed.
 */

export interface ReleaseNote {
	version: string;
	markdown: string;
}

interface VersionedNote extends ReleaseNote {
	parts: [number, number, number];
}

const VERSION_FILE_NAME = /^\.\/(\d+)\.(\d+)\.(\d+)\.md$/;

// Bundled as text at build time, so the drawer never waits on a request.
const files = import.meta.glob<string>("./*.md", {
	query: "?raw",
	import: "default",
	eager: true,
});

function toVersionedNote([path, markdown]: [string, string]) {
	const match = VERSION_FILE_NAME.exec(path);
	if (!match) {
		// Skipped rather than thrown, so a misnamed file can't break the app.
		console.warn(`Release note ${path} skipped: name it X.Y.Z.md`);
		return null;
	}
	const [, major, minor, patch] = match;
	const note: VersionedNote = {
		version: `${major}.${minor}.${patch}`,
		markdown,
		parts: [Number(major), Number(minor), Number(patch)],
	};
	return note;
}

/** Newest first, comparing numerically so that 3.10.0 is newer than 3.9.0. */
function byVersionDescending(a: VersionedNote, b: VersionedNote) {
	return (
		b.parts[0] - a.parts[0] ||
		b.parts[1] - a.parts[1] ||
		b.parts[2] - a.parts[2]
	);
}

export const releaseNotes: readonly ReleaseNote[] = Object.entries(files)
	.map(toVersionedNote)
	.filter((note): note is VersionedNote => note !== null)
	.sort(byVersionDescending)
	.map(({ version, markdown }) => ({ version, markdown }));

export const latestReleaseNote: ReleaseNote | undefined = releaseNotes[0];
